/*
 * Webflow runtime replacement.
 *
 * Production loads Webflow's 767 KB webflow.js. We deliberately do not. This file provides the
 * subset the site actually uses, and nothing more.
 *
 * WHY the subset is what it is: the interaction definitions were not guessed. Webflow ships the
 * site's IX2 payload inside its hosted bundle as
 *   webflow.schunk.5d50ed89225dcbeb.js -> Webflow.require("ix2").init({ events, actionLists, site })
 * That payload (383 events / 46 action lists) was extracted and cross-referenced against every
 * `data-w-id` in src/partials/, src/chrome/ and the built blog template. Every id that ships maps
 * to a click, tab or scroll action list reproduced below. See docs/ENGINE.md for the table.
 *
 * Two findings worth knowing before changing anything here:
 *
 *   1. NO static page uses the `html.w-mod-js:not(.w-mod-ix)` reveal contract. The two ix2Styles
 *      blocks BaseLayout carries (index, blog-index) reference data-w-id values that exist on no
 *      element — orphaned Webflow CSS. `revealIx2InitialStates()` below still implements the
 *      general case, both halves, so the orphan can never strand real content and so the video and
 *      partner templates' slideInBottom/fadeOut presets get the mechanism for free.
 *      The blog template DOES have a real opacity 0 -> 1 reveal, but it carries its initial state
 *      as an inline style rather than head CSS — see `initArticleFade()`, and read its comment
 *      before touching it: the element it hides is the article body.
 *
 *   2. `#cta-desktop-trigger` is clicked *synthetically* by the site engine (cta-desktop.ts fires
 *      a MouseEvent after 10s) and `wfu-modal-close-type="interaction"` makes modal.ts deliberately
 *      do nothing on close. So the desktop QR popup exists only if a-42/a-40 below work. This is
 *      not decoration; it is the desktop install CTA.
 *
 * Not implemented, on purpose:
 *   - Webflow form submission. See docs/ENGINE.md — the footer newsletter form currently POSTs to
 *     Webflow's form API and that is a product decision, not an engine one.
 *   - w-dropdown / w-slider / w-lightbox / w-radio / w-background-video: zero instances in markup.
 *   - The `.navigation` MOUSE_OVER/MOUSE_OUT action lists (a-2/a-3 and their six clones). Both the
 *     "in" and "out" states set the same colour, rgba(0,0,0,1), so they are visual no-ops.
 */

/* -------------------------------------------------------------------------- */
/* helpers                                                                    */
/* -------------------------------------------------------------------------- */

const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.prototype.slice.call(root.querySelectorAll(sel)) as T[];

import { installWebflowShim } from './webflow-shim';

/** Webflow's breakpoints, from the IX2 payload's `site.mediaQueries`. */
const MQ = {
  main: '(min-width: 992px)',
  medium: '(min-width: 768px) and (max-width: 991px)',
  small: '(min-width: 480px) and (max-width: 767px)',
  tiny: '(max-width: 479px)',
} as const;

/** True when the current viewport is in at least one of the named Webflow breakpoints. */
function inBreakpoints(keys: Array<keyof typeof MQ>): boolean {
  return keys.some((k) => window.matchMedia(MQ[k]).matches);
}

const wfPage = (): string => document.documentElement.getAttribute('data-wf-page') || '';

/** Siblings of `el` matching `selector`. Mirrors IX2's `useEventTarget: "SIBLINGS"`. */
function siblings(el: Element, selector: string): HTMLElement[] {
  const parent = el.parentElement;
  if (!parent) return [];
  return Array.prototype.filter.call(
    parent.children,
    (c: Element) => c !== el && c.matches(selector),
  ) as HTMLElement[];
}

/** Nearest ancestor matching `selector`. Mirrors IX2's `useEventTarget: "PARENT"`. */
const parentMatching = (el: Element, selector: string): HTMLElement | null =>
  el.parentElement ? (el.parentElement.closest(selector) as HTMLElement | null) : null;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/* -------------------------------------------------------------------------- */
/* 1. IX2 initial state + scroll reveal                                       */
/* -------------------------------------------------------------------------- */

/*
 * Webflow's contract has two halves and they must ship together:
 *   head CSS  `html.w-mod-js:not(.w-mod-ix) [data-w-id="X"] { opacity:0; transform:...; }`
 *   runtime   adds `w-mod-ix` to <html>, which switches that CSS off, then animates each element in
 *
 * Ship only the CSS and content is invisible forever. Ship only the class and content flashes.
 * So: read the rules back out of the live stylesheets, pin them onto the elements as inline style
 * (that survives `w-mod-ix`), add `w-mod-ix`, then clear the inline style with a transition when
 * the element scrolls into view.
 */
function revealIx2InitialStates(): void {
  const html = document.documentElement;
  type Pending = { el: HTMLElement; declarations: string };
  const pending: Pending[] = [];

  const collect = (rules: CSSRuleList, mediaMatches: boolean) => {
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i] as CSSRule;
      if (rule.type === CSSRule.MEDIA_RULE) {
        const media = rule as CSSMediaRule;
        let matches = mediaMatches;
        try {
          matches = mediaMatches && window.matchMedia(media.conditionText).matches;
        } catch {
          matches = false;
        }
        collect(media.cssRules, matches);
        continue;
      }
      if (rule.type !== CSSRule.STYLE_RULE) continue;
      const style = rule as CSSStyleRule;
      if (style.selectorText.indexOf('w-mod-ix') === -1) continue;
      if (!mediaMatches) continue;
      // `html.w-mod-js:not(.w-mod-ix) [data-w-id="X"]` -> `[data-w-id="X"]`
      const target = style.selectorText.replace(/^.*?\)\s*/, '').trim();
      if (!target) continue;
      let els: HTMLElement[] = [];
      try {
        els = $$(target);
      } catch {
        continue;
      }
      for (const el of els) pending.push({ el, declarations: style.style.cssText });
    }
  };

  for (const sheet of Array.prototype.slice.call(document.styleSheets) as CSSStyleSheet[]) {
    try {
      collect(sheet.cssRules, true);
    } catch {
      // Cross-origin sheet (Webflow CDN in production). Nothing readable, nothing to do.
    }
  }

  for (const { el, declarations } of pending) {
    el.style.cssText = el.style.cssText + ';' + declarations;
    el.setAttribute('data-wf-ix-hidden', '');
  }

  // Turn the head CSS off. From here the inline styles above are the only initial state.
  html.classList.add('w-mod-ix');

  const hidden = $$('[data-wf-ix-hidden]');
  if (!hidden.length) return;

  const reveal = (el: HTMLElement) => {
    el.removeAttribute('data-wf-ix-hidden');
    // `display:none` cannot be transitioned; drop it first, then animate on the next frame.
    el.style.removeProperty('display');
    requestAnimationFrame(() => {
      el.style.transition = 'opacity 500ms ease, transform 500ms ease';
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('-webkit-transform');
      el.style.removeProperty('-moz-transform');
      el.style.removeProperty('-ms-transform');
    });
  };

  if (typeof IntersectionObserver === 'undefined') {
    hidden.forEach(reveal);
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        reveal(entry.target as HTMLElement);
      }
    },
    // Webflow's SCROLL_INTO_VIEW default fires once any part of the element is on screen.
    { threshold: 0 },
  );
  hidden.forEach((el) => io.observe(el));
}

/* -------------------------------------------------------------------------- */
/* 2. Accordion — IX2 a-15 (open) / a-16 (close)                              */
/* -------------------------------------------------------------------------- */

/*
 * a-15: SIBLINGS .accordion-content height -> AUTO (400ms ease); CHILDREN .toggle-arrow rotateZ 0
 * a-16: SIBLINGS .accordion-content height -> 0px  (400ms ease); CHILDREN .toggle-arrow rotateZ -90
 *
 * Collapsed is the markup's own initial state (`<div style="height:0px" class="accordion-content">`
 * and an inline rotateZ(-90deg) on the arrow img), which is why improve-sleep-hygiene ships no
 * ix2Styles block. Do not add one.
 */
const ACCORDION_MS = 400;

/*
 * One-time style for the focusable accordion triggers. See the comment at the setAttribute calls below.
 */
let accordionFocusStyled = false;
function styleAccordionFocus(): void {
  if (accordionFocusStyled) return;
  accordionFocusStyled = true;
  const style = document.createElement('style');
  style.setAttribute('data-rise', 'accordion-focus');
  style.textContent =
    '.accordion-trigger[role="button"]:focus{outline:none}' +
    '.accordion-trigger[role="button"]:focus-visible{outline:revert}';
  document.head.appendChild(style);
}

function initAccordions(): void {
  styleAccordionFocus();
  for (const trigger of $$('.accordion-trigger[data-w-id]')) {
    const content = siblings(trigger, '.accordion-content')[0];
    if (!content) continue;
    const arrow = trigger.querySelector<HTMLElement>('.toggle-arrow');

    /*
     * role/tabindex make the accordion keyboard-operable, which production's Webflow markup is not. That
     * is an improvement worth keeping — but making a <div> focusable also means the UA draws its focus
     * ring after a POINTER click, which production never shows. A visible difference, and the brief for
     * this migration is to change nothing visible.
     *
     * `:focus-visible` is the resolution rather than a compromise: the UA shows the ring for keyboard
     * focus and suppresses it for pointer focus. Injected here rather than added to the stylesheet so
     * the rule lives next to the attributes that create the need for it — remove one, remove both.
     * `outline: revert` (not `none`) keeps the keyboard ring at whatever the UA default is, so this
     * cannot silently become "no focus indicator at all".
     */
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-expanded', 'false');

    let open = false;
    let animating = false;

    const setArrow = (deg: number) => {
      if (!arrow) return;
      arrow.style.transition = `transform ${ACCORDION_MS}ms ease`;
      arrow.style.transform = `translate3d(0, 0, 0) scale3d(1, 1, 1) rotateX(0) rotateY(0) rotateZ(${deg}deg) skew(0, 0)`;
    };

    const toggle = () => {
      if (animating) return;
      animating = true;
      content.style.transition = `height ${ACCORDION_MS}ms ease`;
      if (open) {
        // auto -> measured px -> 0, otherwise the transition has no start value.
        content.style.height = `${content.scrollHeight}px`;
        requestAnimationFrame(() => {
          content.style.height = '0px';
        });
        setArrow(-90);
        open = false;
      } else {
        content.style.height = `${content.scrollHeight}px`;
        setArrow(0);
        open = true;
      }
      trigger.setAttribute('aria-expanded', String(open));
      window.setTimeout(() => {
        // Webflow's heightUnit:"AUTO" leaves the element on `height:auto` so it reflows with
        // late-loading images. Reproduce that rather than pinning the measured pixel height.
        if (open) content.style.height = 'auto';
        animating = false;
      }, ACCORDION_MS);
    };

    trigger.addEventListener('click', toggle);
    trigger.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        toggle();
      }
    });
  }
}

/* -------------------------------------------------------------------------- */
/* 3. Desktop QR popup — IX2 a-42 (open) / a-40 (close)                       */
/* -------------------------------------------------------------------------- */

const POPUP_MS = 500;
const POPUP_OFFSET_X = -550; // px, from a-42/a-40 TRANSFORM_MOVE xValue

function initDesktopCtaPopup(): void {
  const wrappers = $$('.popup-cta_wrapper');
  if (!wrappers.length) return;

  // a-42 group 1 is the initial state (`useFirstGroupAsInitialState: true`): every
  // .popup-cta_wrapper starts transparent and translated off to the left.
  for (const w of wrappers) {
    w.style.opacity = '0';
    w.style.transform = `translate3d(${POPUP_OFFSET_X}px, 0, 0)`;
  }

  for (const trigger of $$('.popup-cta_trigger[data-w-id]')) {
    // e-603 declares mediaQueries ["main","medium"] only: below 768px this click does nothing at
    // all in production, which is what keeps the desktop QR modal off phones.
    trigger.addEventListener('click', () => {
      if (!inBreakpoints(['main', 'medium'])) return;
      for (const w of siblings(trigger, '.popup-cta_wrapper')) w.style.display = 'block';
      requestAnimationFrame(() => {
        for (const w of wrappers) {
          w.style.transition = `transform ${POPUP_MS}ms ease, opacity ${POPUP_MS}ms ease`;
          w.style.transform = 'translate3d(0, 0, 0)';
          w.style.opacity = '1';
        }
      });
    });
  }

  for (const closer of $$('.popup-cta_close-wrapper[data-w-id]')) {
    closer.addEventListener('click', () => {
      // a-40 targets `useEventTarget: "PARENT"` for the move/fade, and "SIBLINGS" for the
      // display:none. In this DOM the close button has no .popup-cta_wrapper sibling, so
      // production never actually sets display:none on close — the panel just animates off to
      // x:-550px and stays in the layout, harmlessly off-screen. Reproduced as-is; do not
      // "fix" it to display:none without checking the parity harness first.
      const wrapper = parentMatching(closer, '.popup-cta_wrapper');
      if (!wrapper) return;
      wrapper.style.transition = `transform ${POPUP_MS}ms ease, opacity ${POPUP_MS}ms ease`;
      wrapper.style.transform = `translate3d(${POPUP_OFFSET_X}px, 0, 0)`;
      wrapper.style.opacity = '0';
    });
  }
}

/* -------------------------------------------------------------------------- */
/* 4. Cookie banner "Customize" — IX2 a-45                                    */
/* -------------------------------------------------------------------------- */

/*
 * The Customize link carries only `ph-event="cc-customize"` and a data-w-id — no `fs-cc`
 * attribute — so Finsweet's consent script does NOT open the preferences panel. IX2 a-45 does.
 * Drop this and the cookie banner has no way to reach per-category consent.
 *
 * a-45 is one-way (MOUSE_CLICK, no MOUSE_SECOND_CLICK counterpart).
 */
const PREFS_MS = 600;
const PREFS_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';
const HIDE_ALL_SELECTOR = '.fs-cc-button.fs-cc-is-secondary.fs-cc-default_hide-all';

function initCookiePrefs(): void {
  const triggers = $$('a[data-w-id][ph-event="cc-customize"]');
  if (!triggers.length) return;

  const prefs = $$('.fs-cc-modal_prefs');

  // a-45 group 1 = initial state.
  for (const trigger of triggers) {
    trigger.style.display = 'inline-block';
    for (const s of siblings(trigger, HIDE_ALL_SELECTOR)) s.style.display = 'none';
  }
  for (const p of prefs) {
    p.style.width = '100%';
    p.style.height = '0px';
    p.style.display = 'none';
  }

  for (const trigger of triggers) {
    trigger.addEventListener('click', (ev) => {
      ev.preventDefault();
      // group 2: swap the button for the Save button, un-hide the panel
      trigger.style.display = 'none';
      for (const s of siblings(trigger, HIDE_ALL_SELECTOR)) s.style.display = 'inline-block';
      for (const p of prefs) p.style.display = 'block';
      // group 3: height 0 -> auto
      requestAnimationFrame(() => {
        for (const p of prefs) {
          p.style.transition = `height ${PREFS_MS}ms ${PREFS_EASING}`;
          p.style.height = `${p.scrollHeight}px`;
          window.setTimeout(() => {
            p.style.height = 'auto';
          }, PREFS_MS);
        }
      });
    });
  }
}

/* -------------------------------------------------------------------------- */
/* 5. Page scroll progress bar — IX2 a-8                                      */
/* -------------------------------------------------------------------------- */

/*
 * `.page-scroll-progress` is `width: 100%` in the site CSS. With no JS it is a permanent
 * full-width purple bar across the top of every page, so this is a required item, not a nicety.
 *
 * a-8 is a SCROLL_PROGRESS continuous action:
 *   keyframe   0 -> width  0vw
 *   keyframe   1 -> bg rgb(114, 28, 255)
 *   keyframe  52 -> bg rgb(253, 130, 6)
 *   keyframe  99 -> bg rgb(114, 28, 255)
 *   keyframe 100 -> width 100vw
 *
 * Gated on the exact set of Webflow page ids that declare the event, from the IX2 payload. Pages
 * outside the set genuinely have no progress animation in production and must not gain one.
 * (`data-wf-page` on <html> is the same id IX2 uses for `appliesTo: "PAGE"` targets.)
 */
const SCROLL_PROGRESS_PAGES = new Set([
  '63ffb310e7044645092740be',
  '63ffb310e704462b1f2740bc',
  '63ffb310e704462e73274147',
  '63ffb310e70446a333274087',
  '63ffb310e70446464c2740dc',
  '63ffb310e704461323274169',
  '63ffb310e70446b2aa2741b7',
  '63ffb310e704464c70274130',
  '63ffb310e7044667352740c1',
  '63ffb310e70446111a27409b',
  '63ffb310e70446fb512740fd',
  '63ffb310e70446f09227412d',
  '63ffb310e70446efb6274197',
  '63ffb310e70446740f274168',
  '63ffb310e704463756274164',
  '63ffb310e70446153e273fef',
  '63ffb310e7044631b42740fc',
  '63ffb310e704467fcb274113',
  '63ffb310e704464d86274044',
  '63ffb310e704467bae2740f4',
  '641e04000bbd0a8a6de6b532',
  '64bc3c36c380dc07d9b1052e',
  '6594bcb4c618eff1ae9e5c9a',
  // SCROLLING_IN_VIEW variants of the same action list, on the CMS templates
  '656c010be0e11e567bae7889',
]);

const PROGRESS_STOPS: Array<{ at: number; rgb: [number, number, number] }> = [
  { at: 1, rgb: [114, 28, 255] },
  { at: 52, rgb: [253, 130, 6] },
  { at: 99, rgb: [114, 28, 255] },
];

function progressColour(pct: number): string {
  if (pct <= PROGRESS_STOPS[0].at) return `rgb(${PROGRESS_STOPS[0].rgb.join(', ')})`;
  const last = PROGRESS_STOPS[PROGRESS_STOPS.length - 1];
  if (pct >= last.at) return `rgb(${last.rgb.join(', ')})`;
  for (let i = 0; i < PROGRESS_STOPS.length - 1; i++) {
    const a = PROGRESS_STOPS[i];
    const b = PROGRESS_STOPS[i + 1];
    if (pct < a.at || pct > b.at) continue;
    const t = (pct - a.at) / (b.at - a.at);
    const c = [0, 1, 2].map((k) => Math.round(lerp(a.rgb[k], b.rgb[k], t)));
    return `rgb(${c.join(', ')})`;
  }
  return `rgb(${last.rgb.join(', ')})`;
}

function initScrollProgress(): void {
  const bars = $$('.page-scroll-progress');
  if (!bars.length) return;
  if (!SCROLL_PROGRESS_PAGES.has(wfPage())) return;

  const update = () => {
    const doc = document.documentElement;
    const scrollable = doc.scrollHeight - doc.clientHeight;
    // Webflow clamps a zero-scroll page to 0%, leaving the bar hidden.
    const pct = scrollable > 0 ? Math.min(100, Math.max(0, (doc.scrollTop / scrollable) * 100)) : 0;
    const colour = progressColour(pct);
    for (const bar of bars) {
      bar.style.width = `${pct}vw`;
      bar.style.backgroundColor = colour;
    }
  };

  update();
  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
}

/* -------------------------------------------------------------------------- */
/* 6. Nav hide / show on scroll — IX2 a-29 / a-30                             */
/* -------------------------------------------------------------------------- */

/*
 * Two page ids only, and neither is a static page we currently build — they are CMS templates
 * (blog / video-or-partner detail). Implemented now because Phase 2 will need it and because it is
 * cheap; dormant everywhere else because of the page-id gate.
 */
const NAV_SCROLL_PAGES = new Set(['63ffb310e704462b1f2740bc', '64d364f481f2d686ad4aec9a']);
const NAV_HIDE_Y = -70; // px, from a-29 TRANSFORM_MOVE yValue
const NAV_SCROLL_MS = 300;

function initNavScrollHide(): void {
  if (!NAV_SCROLL_PAGES.has(wfPage())) return;
  const targets = $$('.navigation-mob, .sticky-top_wrapper-container');
  if (!targets.length) return;

  let lastY = window.scrollY;
  let hidden = false;

  const apply = (y: number) => {
    for (const t of targets) {
      t.style.transition = `transform ${NAV_SCROLL_MS}ms ease`;
      t.style.transform = `translate3d(0, ${y}px, 0)`;
    }
  };

  window.addEventListener(
    'scroll',
    () => {
      const y = window.scrollY;
      const down = y > lastY;
      lastY = y;
      if (down && !hidden) {
        hidden = true;
        apply(NAV_HIDE_Y);
      } else if (!down && hidden) {
        hidden = false;
        apply(0);
      }
    },
    { passive: true },
  );
}

/* -------------------------------------------------------------------------- */
/* 7. Back-to-top fade — IX2 a-23 / a-24                                      */
/* -------------------------------------------------------------------------- */

function initBackToTop(): void {
  const anchors = $$('#Ratings[data-w-id]');
  if (!anchors.length || typeof IntersectionObserver === 'undefined') return;

  for (const anchor of anchors) {
    const wraps = siblings(anchor, '.back-to-top-wrapper');
    if (!wraps.length) continue;
    for (const w of wraps) w.style.opacity = '0'; // a-23 group 1 = initial state

    let outTimer = 0;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            window.clearTimeout(outTimer);
            for (const w of wraps) {
              w.style.transition = 'opacity 1000ms ease';
              w.style.opacity = '1';
            }
          } else {
            // a-24 carries delay:1000ms before the 1000ms fade.
            outTimer = window.setTimeout(() => {
              for (const w of wraps) {
                w.style.transition = 'opacity 1000ms ease';
                w.style.opacity = '0';
              }
            }, 1000);
          }
        }
      },
      { threshold: 0 },
    );
    io.observe(anchor);
  }
}

/* -------------------------------------------------------------------------- */
/* 8. Blog Table of Contents popout — IX2 a-35 (open) / a-36 (close)          */
/* -------------------------------------------------------------------------- */

/*
 * Blog-template only (`data-wf-page="63ffb310e704462b1f2740bc"`), so it was dormant until the CMS
 * routes landed — 229 posts now render it. Two tabs share one panel:
 *
 *   .toc_popout                        translateX 100% <-> 0        (200ms)
 *     .toc_popout-tab.is-open-action   display flex <-> none
 *     .toc_popout-tab.is-close-action  display none <-> flex
 *     .toc_wrapper                     width 0% <-> 95%             (200ms)
 *       .toc_wrapper-inner             opacity 0 <-> 1              (100ms)
 *
 * All of the closed-state values already ship as inline styles in the template markup
 * (`style="width:0%"`, `style="opacity:0"`, the translate3d(100%) on .toc_popout), which is why
 * a-35's `useFirstGroupAsInitialState` needs nothing applied here. Only the toggles are missing.
 *
 * IX2 runs actionItemGroups sequentially, so open is 200ms of movement and only then 100ms of
 * fade-in; close is the reverse. The delays below reproduce that ordering, not just the durations.
 */
function initTocPopout(): void {
  const openTabs = $$('.toc_popout-tab.is-open-action[data-w-id]');
  if (!openTabs.length) return;

  for (const openTab of openTabs) {
    const popout = parentMatching(openTab, '.toc_popout');
    if (!popout) continue;
    const closeTab = siblings(openTab, '.toc_popout-tab.is-close-action')[0];
    const wrapper = siblings(openTab, '.toc_wrapper')[0];
    const inner = wrapper ? wrapper.querySelector<HTMLElement>('.toc_wrapper-inner') : null;
    if (!closeTab || !wrapper) continue;

    openTab.setAttribute('role', 'button');
    openTab.setAttribute('tabindex', '0');
    closeTab.setAttribute('role', 'button');
    closeTab.setAttribute('tabindex', '0');

    const open = () => {
      closeTab.style.display = 'flex';
      openTab.style.display = 'none';
      popout.style.transition = 'transform 200ms ease';
      popout.style.transform = 'translate3d(0%, 0, 0)';
      wrapper.style.transition = 'width 200ms ease';
      wrapper.style.width = '95%';
      if (inner) {
        // Group 3 starts after group 2 finishes; without the delay the text fades in over a panel
        // that is still sliding, which is not what production does.
        inner.style.transition = 'opacity 100ms ease 200ms';
        inner.style.opacity = '1';
      }
    };

    const close = () => {
      if (inner) {
        inner.style.transition = 'opacity 100ms ease';
        inner.style.opacity = '0';
      }
      const after = () => {
        openTab.style.display = 'flex';
        closeTab.style.display = 'none';
        wrapper.style.transition = 'width 200ms ease';
        wrapper.style.width = '0%';
        popout.style.transition = 'transform 200ms ease';
        popout.style.transform = 'translate3d(100%, 0, 0)';
      };
      inner ? window.setTimeout(after, 100) : after();
    };

    const bind = (el: HTMLElement, fn: () => void) => {
      el.addEventListener('click', fn);
      el.addEventListener('keydown', (ev) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          fn();
        }
      });
    };
    bind(openTab, open);
    bind(closeTab, close);
  }
}

/* -------------------------------------------------------------------------- */
/* 9. Article body fade-in — IX2 a-9 "show-share-icons"                       */
/* -------------------------------------------------------------------------- */

/*
 * Blog template. Targets `.rich-text` and `.post-summary_rich-text` — the article body itself.
 * a-9 has an empty `target: {}`, i.e. the event target, and is opacity 0 -> 1 over 500ms on
 * SCROLL_INTO_VIEW.
 *
 * This one gets a failsafe that nothing else here needs, and the reason matters: the initial state
 * is opacity:0 on the ARTICLE BODY. Get the reveal wrong and a 2,000-word post renders blank while
 * still passing every HTML fingerprint check, because the text is all there in the markup. So each
 * region is also revealed unconditionally after 10s.
 *
 * 10s, not 1s, on purpose. On a 1440x900 viewport both regions start below the fold (top 1055px and
 * 1498px measured), so a short timer would fire before the reader ever scrolls and the fade would
 * effectively never run — a behaviour change. 10s is far outside any real reading interaction, so
 * it only ever fires when the observer genuinely failed.
 */
const ARTICLE_FADE_MS = 500;
const ARTICLE_FADE_FAILSAFE_MS = 10000;

function initArticleFade(): void {
  const targets = $$('.rich-text[data-w-id], .post-summary_rich-text[data-w-id]');
  // No IntersectionObserver means no reveal path, so do not hide anything in the first place.
  if (!targets.length || typeof IntersectionObserver === 'undefined') return;

  const reveal = (el: HTMLElement) => {
    if (!el.hasAttribute('data-wf-fade-pending')) return;
    el.removeAttribute('data-wf-fade-pending');
    el.style.transition = `opacity ${ARTICLE_FADE_MS}ms ease`;
    el.style.opacity = '1';
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        reveal(entry.target as HTMLElement);
      }
    },
    { threshold: 0 },
  );

  for (const el of targets) {
    // Observe BEFORE hiding, and revert on any failure: an element must never be left at opacity 0
    // with nothing watching it.
    try {
      io.observe(el);
      window.setTimeout(() => reveal(el), ARTICLE_FADE_FAILSAFE_MS);
      el.setAttribute('data-wf-fade-pending', '');
      el.style.opacity = '0';
    } catch (error) {
      el.removeAttribute('data-wf-fade-pending');
      el.style.removeProperty('opacity');
      console.error('[interactions] article-fade could not arm; left visible:', error);
    }
  }
}

/* -------------------------------------------------------------------------- */
/* 10. w-tabs                                                                 */
/* -------------------------------------------------------------------------- */

/*
 * One instance on `/` (`.feature-tabs`, data-duration-in/out = 0), plus fb and fb2. Webflow pairs
 * a `.w-tab-link[data-w-tab]` with the `.w-tab-pane[data-w-tab]` of the same name and moves
 * `w--current` / `w--tab-active`.
 *
 * On fb/fb2 the tab links also carry data-w-id and drive IX2 a-17/a-18 (and a-21/a-22 for the
 * "-2" variant), which recolour `.tab-icon-circle{,-2}` and `.icon-tab-text` on activate. Those
 * are applied here as inline styles at the same time as the class change, because there is no
 * `w--current` CSS rule for them.
 */
type TabColours = { bg: string; border: string; text: string };
const TAB_ACTIVE: TabColours = { bg: 'rgb(255, 255, 255)', border: 'rgb(255, 255, 255)', text: 'rgb(255, 255, 255)' };
const TAB_INACTIVE: TabColours = { bg: 'rgba(0, 0, 0, 0)', border: 'rgb(142, 88, 255)', text: 'rgb(142, 88, 255)' };

function paintTab(link: HTMLElement, colours: TabColours): void {
  if (!link.hasAttribute('data-w-id')) return;
  for (const circle of $$('.tab-icon-circle, .tab-icon-circle-2', link)) {
    circle.style.transition = 'background-color 100ms ease, border-color 200ms ease';
    circle.style.backgroundColor = colours.bg;
    circle.style.borderColor = colours.border;
  }
  for (const text of $$('.icon-tab-text', link)) {
    text.style.transition = 'color 200ms ease';
    text.style.color = colours.text;
  }
}

function initTabs(): void {
  // Component counter, so ids match Webflow's w-tabs-{n}-... numbering when a page has several.
  let tabIndex = 0;
  for (const tabs of $$('.w-tabs')) {
    const menu = tabs.querySelector('.w-tab-menu');
    const content = tabs.querySelector('.w-tab-content');
    if (!menu || !content) continue;

    const links = $$('.w-tab-link', menu);
    const panes = $$('.w-tab-pane', content);
    if (!links.length) continue;

    const durationIn = Number(tabs.getAttribute('data-duration-in') || 0);
    const durationOut = Number(tabs.getAttribute('data-duration-out') || 0);
    const easing = tabs.getAttribute('data-easing') || 'ease';

    /*
     * ARIA wiring, matching what webflow.js emits at runtime. Captured from live production rather than
     * written from the spec, because the ids are part of the contract:
     *
     *   .w-tab-menu   role="tablist"
     *   .w-tab-link   id="w-tabs-{n}-data-w-tab-{i}"  href="#w-tabs-{n}-data-w-pane-{i}"
     *                 role="tab"  aria-controls=<pane id>  aria-selected
     *   .w-tab-pane   id="w-tabs-{n}-data-w-pane-{i}"  role="tabpanel"  aria-labelledby=<link id>
     *
     * We previously set only role/aria-selected/tabindex, so the tab -> panel RELATIONSHIP was missing
     * and a screen reader could not tell which panel a tab controlled. That is a regression against
     * production, not merely a difference — production has this and we did not.
     *
     * Also corrects a mistake: `role="tabpanel"` belongs on each PANE, not on `.w-tab-content`. We had
     * it on the container, which announces one giant panel instead of one per tab.
     *
     * `n` is the index of this tab component on the page, matching Webflow's numbering.
     */
    const n = tabIndex++;
    menu.setAttribute('role', 'tablist');
    content.removeAttribute('role');

    links.forEach((link, i) => {
      const linkId = `w-tabs-${n}-data-w-tab-${i}`;
      const paneId = `w-tabs-${n}-data-w-pane-${i}`;
      link.id = linkId;
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-controls', paneId);
      if (!link.getAttribute('href')) link.setAttribute('href', `#${paneId}`);
    });
    panes.forEach((pane, i) => {
      pane.id = `w-tabs-${n}-data-w-pane-${i}`;
      pane.setAttribute('role', 'tabpanel');
      pane.setAttribute('aria-labelledby', `w-tabs-${n}-data-w-tab-${i}`);
    });

    const activate = (link: HTMLElement) => {
      const name = link.getAttribute('data-w-tab');
      if (!name) return;
      for (const l of links) {
        const on = l === link;
        l.classList.toggle('w--current', on);
        l.setAttribute('aria-selected', String(on));
        l.setAttribute('tabindex', on ? '0' : '-1');
        paintTab(l, on ? TAB_ACTIVE : TAB_INACTIVE);
      }
      for (const pane of panes) {
        const on = pane.getAttribute('data-w-tab') === name;
        if (on) {
          pane.classList.add('w--tab-active');
          if (durationIn > 0) {
            pane.style.opacity = '0';
            pane.style.transition = `opacity ${durationIn}ms ${easing}`;
            requestAnimationFrame(() => {
              pane.style.opacity = '1';
            });
          } else {
            pane.style.removeProperty('opacity');
            pane.style.removeProperty('transition');
          }
        } else {
          pane.classList.remove('w--tab-active');
          if (durationOut > 0) pane.style.removeProperty('opacity');
        }
      }
      tabs.setAttribute('data-current', name);
    };

    for (const link of links) {
      link.setAttribute('role', 'tab');
      const isCurrent = link.classList.contains('w--current');
      link.setAttribute('aria-selected', String(isCurrent));
      link.setAttribute('tabindex', isCurrent ? '0' : '-1');
      paintTab(link, isCurrent ? TAB_ACTIVE : TAB_INACTIVE);

      link.addEventListener('click', (ev) => {
        ev.preventDefault();
        activate(link);
      });
      link.addEventListener('keydown', (ev) => {
        const idx = links.indexOf(link);
        let next = -1;
        if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') next = (idx + 1) % links.length;
        else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') next = (idx - 1 + links.length) % links.length;
        else if (ev.key === 'Home') next = 0;
        else if (ev.key === 'End') next = links.length - 1;
        else if (ev.key === 'Enter' || ev.key === ' ') {
          ev.preventDefault();
          activate(link);
          return;
        }
        if (next < 0) return;
        ev.preventDefault();
        links[next].focus();
        activate(links[next]);
      });
    }
  }
}

/* -------------------------------------------------------------------------- */
/* 11. w-nav (mobile navigation)                                              */
/* -------------------------------------------------------------------------- */

/*
 * HIGHEST-RISK ITEM. If this silently breaks, mobile navigation is dead and no HTML parity check
 * would notice. Ported from Webflow's own `navbar` module (live chunk
 * webflow.schunk.5d50ed89225dcbeb.js, `a.define("navbar", ...)`), not invented.
 *
 * The chrome ships two `.w-nav` instances:
 *   .navigation      data-collapse="medium", NO .w-nav-button  -> nothing to open, inert
 *   .navigation-mob  data-collapse="all",    .menu-mob.w-nav-button -> the real mobile nav
 *
 * The mechanics that the CSS depends on, and that are easy to get wrong:
 *   - an overlay `<div class="w-nav-overlay" data-wf-ignore>` is created at init and appended to
 *     the .w-nav element. `.w-nav-overlay { display:none; overflow:hidden; top:100% }`.
 *   - on open the menu is MOVED into the overlay and given `data-nav-menu-open`, whose CSS
 *     (`display:block !important; position:absolute; top:100%`) is what actually shows it; the
 *     overlay's own rule then pins it to `top: 0`.
 *   - the menu is animated in with `transform: translateY(-(navHeight + menuHeight)) -> 0`.
 *   - on close the menu is moved back to its original position, so a second open still works.
 * Skipping the move-into-overlay step leaves the menu visible but clipped by the fixed 70/85px
 * tall `.navigation-mob`, i.e. an empty black strip. That is the failure mode to watch for.
 */
interface NavState {
  el: HTMLElement;
  menu: HTMLElement;
  button: HTMLElement;
  overlay: HTMLDivElement;
  parent: HTMLElement;
  prevSibling: Node | null;
  links: HTMLElement[];
  open: boolean;
  duration: number;
  easing: string;
  easing2: string;
  animOver: boolean;
  animDirect: number;
  docHeight: boolean;
  onOutside: (ev: Event) => void;
}

function initNavbars(): void {
  const states: NavState[] = [];

  for (let i = 0; i < $$('.w-nav').length; i++) {
    const el = $$('.w-nav')[i];
    const menu = el.querySelector<HTMLElement>('.w-nav-menu');
    const button = el.querySelector<HTMLElement>('.w-nav-button');
    if (!menu || !button) continue; // .navigation has no button: inert, exactly as in production

    const animation = el.getAttribute('data-animation') || 'default';
    const overlay = document.createElement('div');
    overlay.className = 'w-nav-overlay';
    overlay.setAttribute('data-wf-ignore', '');
    overlay.id = `w-nav-overlay-${i}`;
    el.appendChild(overlay);

    const brand = el.querySelector<HTMLElement>('.w-nav-brand');
    if (brand && brand.getAttribute('href') === '/' && !brand.getAttribute('aria-label')) {
      brand.setAttribute('aria-label', 'home');
    }
    button.setAttribute('style', '-webkit-user-select: text;');
    if (!button.getAttribute('aria-label')) button.setAttribute('aria-label', 'menu');
    button.setAttribute('role', 'button');
    button.setAttribute('tabindex', '0');
    button.setAttribute('aria-controls', overlay.id);
    button.setAttribute('aria-haspopup', 'menu');
    button.setAttribute('aria-expanded', 'false');

    const durationAttr = el.getAttribute('data-duration');

    const state: NavState = {
      el,
      menu,
      button,
      overlay,
      parent: menu.parentElement as HTMLElement,
      prevSibling: null,
      links: $$('.w-nav-link', menu),
      open: false,
      duration: durationAttr != null ? Number(durationAttr) : 400,
      easing: el.getAttribute('data-easing') || 'ease',
      easing2: el.getAttribute('data-easing2') || 'ease',
      animOver: /^over/.test(animation),
      animDirect: /left$/.test(animation) ? -1 : 1,
      docHeight: el.hasAttribute('data-doc-height'),
      onOutside: () => undefined,
    };

    state.onOutside = (ev: Event) => {
      if (!state.open) return;
      const target = ev.target as Element | null;
      if (!target) return;
      // Clicks inside the menu are navigation; clicks on the hamburger are the button's own job.
      if (target.closest('.w-nav-menu') === state.menu) return;
      if (state.button.contains(target)) return;
      closeNav(state);
    };

    button.addEventListener('click', () => (state.open ? closeNav(state) : openNav(state)));
    button.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        ev.stopPropagation();
        state.open ? closeNav(state) : openNav(state);
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        closeNav(state);
      }
    });
    // Webflow closes the menu when an in-page anchor link is tapped.
    menu.addEventListener('click', (ev) => {
      const link = (ev.target as Element | null)?.closest('a');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (href.indexOf('#') === 0 && state.open) closeNav(state);
    });
    el.addEventListener('keydown', (ev) => {
      if (state.open && ev.key === 'Escape') {
        ev.preventDefault();
        closeNav(state);
        state.button.focus();
      }
    });

    states.push(state);
  }

  if (!states.length) return;

  // Webflow force-closes an open menu when the viewport grows past the collapse breakpoint,
  // i.e. when the hamburger stops being displayed.
  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      for (const state of states) {
        const collapsed = window.getComputedStyle(state.button).display !== 'none';
        if (state.open && !collapsed) closeNav(state, true);
      }
    }, 100);
  });
}

/** Height Webflow gives the overlay: document height, less the navbar unless it is fixed. */
function overlayHeight(state: NavState): number {
  let h = state.docHeight ? document.documentElement.scrollHeight : document.body.offsetHeight;
  if (state.animOver) {
    state.menu.style.height = `${h}px`;
  } else if (window.getComputedStyle(state.el).position !== 'fixed') {
    h -= state.el.offsetHeight;
  }
  state.overlay.style.height = `${h}px`;
  return h;
}

function openNav(state: NavState): void {
  if (state.open) return;
  state.open = true;

  state.menu.setAttribute('data-nav-menu-open', '');
  state.links.forEach((l) => l.classList.add('w--nav-link-open'));
  state.button.classList.add('w--open');
  state.button.setAttribute('aria-expanded', 'true');

  const instant = state.duration <= 0;
  const height = overlayHeight(state);
  const menuHeight = state.menu.offsetHeight;
  const menuWidth = state.menu.offsetWidth;
  const navHeight = state.el.clientHeight;

  /*
   * Registered on a later task, NOT synchronously. The click that opened the menu is still
   * bubbling: add the listener now and it fires for that same click, sees a target outside
   * .w-nav-menu, and closes the menu again — the nav appears completely dead. Webflow dodges this
   * by routing its button handler through `t.debounce`, which defers the open past the current
   * dispatch. Same effect, stated explicitly.
   */
  window.setTimeout(() => {
    if (state.open) document.addEventListener('click', state.onOutside);
  }, 0);

  // Move the menu into the overlay, remembering where it came from so close() can put it back.
  state.prevSibling = state.menu.previousSibling;
  state.parent = state.menu.parentElement as HTMLElement;
  state.overlay.style.display = 'block';
  state.overlay.appendChild(state.menu);
  if (state.animOver) state.overlay.style.width = `${menuWidth}px`;

  if (instant) return;

  const from = state.animOver
    ? `translate3d(${state.animDirect * menuWidth}px, 0, 0)`
    : `translate3d(0, ${-(navHeight + menuHeight)}px, 0)`;
  if (state.animOver) state.menu.style.height = `${height}px`;
  state.menu.style.transition = 'none';
  state.menu.style.transform = from;
  // Force a style flush so the transition has a start value to animate from.
  void state.menu.offsetHeight;
  state.menu.style.transition = `transform ${state.duration}ms ${state.easing}`;
  state.menu.style.transform = 'translate3d(0, 0, 0)';
}

function closeNav(state: NavState, immediate = false): void {
  if (!state.open) return;
  state.open = false;
  state.button.classList.remove('w--open');
  document.removeEventListener('click', state.onOutside);

  const finish = () => {
    state.menu.style.removeProperty('height');
    state.menu.style.removeProperty('transition');
    state.menu.style.removeProperty('transform');
    state.menu.removeAttribute('data-nav-menu-open');
    state.links.forEach((l) => l.classList.remove('w--nav-link-open'));
    if (state.overlay.children.length) {
      if (state.prevSibling && state.prevSibling.parentNode === state.parent) {
        state.parent.insertBefore(state.menu, state.prevSibling.nextSibling);
      } else {
        state.parent.insertBefore(state.menu, state.parent.firstChild);
      }
      state.overlay.setAttribute('style', '');
      state.overlay.style.display = 'none';
    }
    state.button.setAttribute('aria-expanded', 'false');
  };

  if (immediate || state.duration <= 0) {
    finish();
    return;
  }

  const menuHeight = state.menu.offsetHeight;
  const menuWidth = state.menu.offsetWidth;
  const navHeight = state.el.clientHeight;
  const to = state.animOver
    ? `translate3d(${menuWidth * state.animDirect}px, 0, 0)`
    : `translate3d(0, ${-(navHeight + menuHeight)}px, 0)`;

  state.menu.style.transition = `transform ${state.duration}ms ${state.easing2}`;
  state.menu.style.transform = to;
  window.setTimeout(finish, state.duration);
}

/* -------------------------------------------------------------------------- */
/* 12. w-form: custom checkbox / radio state classes                          */
/* -------------------------------------------------------------------------- */

/*
 * Webflow's real checkbox/radio inputs are visually hidden (`opacity:0;position:absolute`) and the
 * visible control is a sibling `.w-checkbox-input`, styled off the class `w--redirected-checked`
 * rather than `:checked`. webflow.js is what toggles that class. Without it the Finsweet cookie
 * preference toggles never appear to change, which reads as "consent controls are broken".
 *
 * This is the ONLY part of Webflow's forms module reproduced. Submission is not — see docs/ENGINE.md.
 */
const REDIRECTED_CHECKED = 'w--redirected-checked';
const REDIRECTED_FOCUS = 'w--redirected-focus';
const REDIRECTED_FOCUS_VISIBLE = 'w--redirected-focus-visible';

function customControls(input: HTMLInputElement): HTMLElement[] {
  const cls = input.type === 'radio' ? '.w-radio-input' : '.w-checkbox-input';
  return siblings(input, cls);
}

function initFormControls(): void {
  const inputs = $$<HTMLInputElement>(
    '.w-form form input[type="checkbox"]:not(.w-checkbox-input), .w-form form input[type="radio"]:not(.w-radio-input)',
  );
  if (!inputs.length) return;

  const sync = (input: HTMLInputElement) => {
    if (input.type === 'radio' && input.name) {
      const group = $$<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(input.name)}"]`);
      for (const r of group) {
        for (const c of customControls(r)) c.classList.toggle(REDIRECTED_CHECKED, r.checked);
      }
      return;
    }
    for (const c of customControls(input)) c.classList.toggle(REDIRECTED_CHECKED, input.checked);
  };

  for (const input of inputs) {
    sync(input); // markup can ship pre-checked
    input.addEventListener('change', () => sync(input));
    input.addEventListener('focus', () => {
      for (const c of customControls(input)) {
        c.classList.add(REDIRECTED_FOCUS);
        if (input.matches(':focus-visible')) c.classList.add(REDIRECTED_FOCUS_VISIBLE);
      }
    });
    input.addEventListener('blur', () => {
      for (const c of customControls(input)) {
        c.classList.remove(REDIRECTED_FOCUS);
        c.classList.remove(REDIRECTED_FOCUS_VISIBLE);
      }
    });
  }
}

/* -------------------------------------------------------------------------- */
/* bootstrap                                                                  */
/* -------------------------------------------------------------------------- */

function initInteractions(): void {
  const steps: Array<[string, () => void]> = [
    ['navbar', initNavbars],
    ['tabs', initTabs],
    ['accordions', initAccordions],
    ['desktop-cta-popup', initDesktopCtaPopup],
    ['cookie-prefs', initCookiePrefs],
    ['scroll-progress', initScrollProgress],
    ['nav-scroll-hide', initNavScrollHide],
    ['back-to-top', initBackToTop],
    ['toc-popout', initTocPopout],
    ['article-fade', initArticleFade],
    ['form-controls', initFormControls],
    // Last: adding `w-mod-ix` invalidates the head initial-state CSS, so everything that reads
    // computed geometry should have run first.
    ['ix2-reveal', revealIx2InitialStates],
  ];
  for (const [name, fn] of steps) {
    try {
      fn();
    } catch (error) {
      // One broken component must not take the rest of the page's interactivity with it.
      console.error(`[interactions] ${name} failed:`, error);
    }
  }
}

/*
 * Exposed deliberately, the way Webflow exposes `Webflow.require(...)`: the scroll-reveal path has
 * no live instance on any shipped page, so the only way to prove both halves of it work is to
 * inject a synthetic initial-state rule and re-run init. Also the escape hatch for anything that
 * injects Webflow markup after load.
 */
declare global {
  interface Window {
    RiseInteractions?: { init: () => void };
  }
}

/*
 * Installed at module-eval time — deliberately before the DOMContentLoaded handler below and before
 * the deferred Finsweet scripts execute, so their `Webflow.push(...)` calls land on a real
 * implementation instead of accumulating in a plain array nobody drains. See webflow-shim.ts for what
 * that silently broke.
 */
installWebflowShim();
window.RiseInteractions = { init: initInteractions };

/*
 * Registered at module-eval time, which is BEFORE vendor/rise-webflow/src/index.ts registers its
 * own DOMContentLoaded handler (see src/lib/engine.ts import order). That ordering matters:
 * cta-desktop.ts's 10s synthetic click and modal.ts's un-hiding of [wfu-modal-trigger] both assume
 * the IX2 initial states are already in place, exactly as webflow.js runs before the engine script
 * in production.
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInteractions);
} else {
  initInteractions();
}

export { initInteractions };
